import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, Calendar, Users, Bed, Phone, Mail, User, 
  MessageSquare, Loader2, CheckCircle, Sparkles,
  ExternalLink, Gift
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format, addDays } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_JINI_URL = 'https://thehotelemerald.bookingjini.in/';

// Room options for the dropdown
const ROOM_OPTIONS = [
  { value: 'any', label: 'No Preference' },
  { value: 'emerald-twin-queen', label: 'Emerald Twin Queen - ₹2,499/night' },
  { value: 'emerald-crystal-deluxe', label: 'Emerald Crystal Deluxe - ₹2,499/night' },
  { value: 'emerald-king', label: 'Emerald King - ₹2,499/night' },
  { value: 'emerald-amber-hill-view', label: 'Emerald Amber Hill View - ₹3,999/night' },
  { value: 'emerald-executive-premium', label: 'Emerald Executive Premium - ₹4,999/night' },
  { value: 'emerald-sapphire-suite', label: 'Emerald Sapphire Suite - ₹6,999/night' },
];

export function BookingInquiryModal({ open, onOpenChange, prefillRoom = null, source = 'website' }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [reference, setReference] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    checkIn: null,
    checkOut: null,
    roomPreference: prefillRoom || 'any',
    adults: '2',
    children: '0',
    specialRequests: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.mobile || !formData.email || !formData.checkIn || !formData.checkOut) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/booking-inquiry`, {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        check_in: format(formData.checkIn, 'yyyy-MM-dd'),
        check_out: format(formData.checkOut, 'yyyy-MM-dd'),
        room_preference: formData.roomPreference === 'any' ? null : formData.roomPreference,
        adults: parseInt(formData.adults),
        children: parseInt(formData.children),
        special_requests: formData.specialRequests || null,
        source: source,
      });
      
      setReference(response.data.reference);
      setIsSuccess(true);
      toast.success('Booking inquiry submitted successfully!');
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      checkIn: null,
      checkOut: null,
      roomPreference: prefillRoom || 'any',
      adults: '2',
      children: '0',
      specialRequests: '',
    });
    setIsSuccess(false);
    setReference('');
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetForm, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="booking-inquiry-modal">
        {/* Header */}
        <div className="bg-emerald-900 text-white px-6 py-4">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Gift className="w-5 h-5 text-gold" />
              <span className="text-gold text-sm font-medium">Save up to 15%</span>
            </div>
            <DialogTitle className="font-serif text-xl sm:text-2xl">Request Direct Booking</DialogTitle>
            <p className="text-emerald-200 text-sm mt-1">
              Our team will contact you within 2 hours with exclusive rates
            </p>
          </DialogHeader>
        </div>

        {isSuccess ? (
          /* Success State */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 text-center"
          >
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-serif text-xl text-foreground mb-2">Inquiry Submitted!</h3>
            <p className="text-muted-foreground mb-4">
              Your reference number is: <strong className="text-foreground">{reference}</strong>
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Our reservations team will contact you at <strong>{formData.mobile}</strong> within 2 hours 
              with the best available rates and exclusive direct booking benefits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleClose} className="btn-emerald">
                Done
              </Button>
              <Button variant="outline" onClick={() => window.open(BOOKING_JINI_URL, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Book Instantly Instead
              </Button>
            </div>
          </motion.div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Name & Mobile */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-muted-foreground" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="inquiry-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                  Mobile Number *
                </Label>
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  data-testid="inquiry-mobile"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="inquiry-email"
              />
            </div>

            {/* Dates */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                  Check-in Date *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="inquiry-checkin"
                    >
                      {formData.checkIn ? format(formData.checkIn, 'MMM dd, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.checkIn}
                      onSelect={(date) => setFormData({ ...formData, checkIn: date, checkOut: date ? addDays(date, 1) : null })}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                  Check-out Date *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="inquiry-checkout"
                    >
                      {formData.checkOut ? format(formData.checkOut, 'MMM dd, yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.checkOut}
                      onSelect={(date) => setFormData({ ...formData, checkOut: date })}
                      disabled={(date) => date <= (formData.checkIn || new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Room & Guests */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2 sm:col-span-1">
                <Label className="flex items-center gap-1.5">
                  <Bed className="w-3.5 h-3.5 text-muted-foreground" />
                  Room Type
                </Label>
                <Select value={formData.roomPreference} onValueChange={(val) => setFormData({ ...formData, roomPreference: val })}>
                  <SelectTrigger data-testid="inquiry-room">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_OPTIONS.map((room) => (
                      <SelectItem key={room.value} value={room.value}>{room.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  Adults
                </Label>
                <Select value={formData.adults} onValueChange={(val) => setFormData({ ...formData, adults: val })}>
                  <SelectTrigger data-testid="inquiry-adults">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <SelectItem key={n} value={n.toString()}>{n} Adult{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  Children
                </Label>
                <Select value={formData.children} onValueChange={(val) => setFormData({ ...formData, children: val })}>
                  <SelectTrigger data-testid="inquiry-children">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4].map((n) => (
                      <SelectItem key={n} value={n.toString()}>{n} Child{n !== 1 ? 'ren' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Special Requests */}
            <div className="space-y-2">
              <Label htmlFor="requests" className="flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                Special Requests (optional)
              </Label>
              <Textarea
                id="requests"
                placeholder="Any special requirements, celebrations, dietary needs..."
                value={formData.specialRequests}
                onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                rows={3}
                data-testid="inquiry-requests"
              />
            </div>

            {/* Benefits Banner */}
            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-sm">
              <p className="font-medium text-emerald-800 dark:text-emerald-200 mb-1 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                Direct Booking Benefits
              </p>
              <ul className="text-emerald-700 dark:text-emerald-300 text-xs space-y-0.5">
                <li>✓ Best price guarantee - up to 15% savings</li>
                <li>✓ Complimentary breakfast & welcome drink</li>
                <li>✓ Flexible cancellation policy</li>
              </ul>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-emerald flex-1"
                data-testid="inquiry-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Inquiry'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(BOOKING_JINI_URL, '_blank')}
                className="flex-1"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Instant Book
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Reusable button to trigger booking inquiry
export function BookDirectButton({ className = '', variant = 'default', roomId = null, source = 'website', children }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className={variant === 'gold' ? `btn-gold ${className}` : `btn-emerald ${className}`}
        data-testid="book-direct-btn"
      >
        {children || 'Book Direct & Save'}
      </Button>
      <BookingInquiryModal 
        open={open} 
        onOpenChange={setOpen} 
        prefillRoom={roomId}
        source={source}
      />
    </>
  );
}
